import { useEffect, useState } from "react";
import axios from "axios";
import { useForm, Controller, useWatch } from "react-hook-form";
import { InputAdornment, Switch, TextField, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
import { NumericFormat } from "react-number-format";
import { Navigation, Package, Save, Tag, Truck } from "react-feather";
import { authData } from "../../components/GetAuth";
import styles from "./companyprofile.module.css";

const SERVICES = [
  { key: "collection", title: "Collection charges", subtitle: "Charged when a driver collects goods from the customer.", icon: <Package /> },
  { key: "delivery", title: "Delivery charges", subtitle: "Charged when goods are delivered to the recipient.", icon: <Truck /> },
];

const emptyCharge = {
  enabled: false,
  type: "fixed",
  fixed_amount: "",
  rate_per_mile: "",
  base_miles: "",
  base_charge: "",
  minimum_charge: "",
};

const toFormCharge = (charge) => {
  const value = { ...emptyCharge, ...(charge || {}) };
  Object.keys(value).forEach((k) => {
    if (value[k] === null) value[k] = "";
  });
  value.enabled = !!value.enabled;
  return value;
};

const num = (v) => (v === "" || v === null || v === undefined ? 0 : Number(v));
const gbp = (v) => `£${Number(v || 0).toFixed(2)}`;

// Mirrors how the charge should be applied: base charge covers the first `base_miles`,
// each extra mile is billed at `rate_per_mile`, and the result never drops below `minimum_charge`.
export const calculateCharge = (charge, miles) => {
  if (!charge?.enabled) return { total: 0, breakdown: "Charge is turned off." };

  if (charge.type === "fixed") {
    return { total: num(charge.fixed_amount), breakdown: "Flat rate, regardless of distance." };
  }

  const baseMiles = num(charge.base_miles);
  const extraMiles = Math.max(0, num(miles) - baseMiles);
  const subtotal = num(charge.base_charge) + extraMiles * num(charge.rate_per_mile);
  const minimum = num(charge.minimum_charge);
  const total = Math.max(subtotal, minimum);

  let breakdown = `${gbp(charge.base_charge)} base`;
  if (baseMiles > 0) breakdown += ` (covers first ${baseMiles} mi)`;
  breakdown += ` + ${extraMiles} mi × ${gbp(charge.rate_per_mile)}`;
  if (minimum > subtotal) breakdown += ` → raised to minimum ${gbp(minimum)}`;

  return { total, breakdown };
};

const MoneyField = ({ control, name, label, requiredType, suffix, errors, hint }) => {
  const serviceKey = name.split(".")[0];
  const error = name.split(".").reduce((o, k) => o?.[k], errors);
  return (<div>
    <Controller
      name={name}
      control={control}
      // Only required while the charge is on and this field belongs to the selected type
      rules={{ validate: (v, values) => !requiredType || !values?.[serviceKey]?.enabled || values?.[serviceKey]?.type !== requiredType || v !== "" || `${label.replace("*", "")} is required` }}
      render={({ field: { onChange, ...field } }) => (
        <NumericFormat
          {...field}
          onValueChange={(v) => onChange(v.value)}
          customInput={TextField}
          label={label}
          variant="outlined"
          fullWidth
          allowNegative={false}
          decimalScale={2}
          inputMode="decimal"
          error={!!error}
          InputProps={suffix === "mi"
            ? { endAdornment: <InputAdornment position="end">miles</InputAdornment> }
            : { startAdornment: <InputAdornment position="start">£</InputAdornment>, ...(suffix ? { endAdornment: <InputAdornment position="end">{suffix}</InputAdornment> } : {}) }}
        />
      )}
    />
    {error
      ? <span className={`${styles.FieldError}`}>{error.message}</span>
      : hint && <span className={`${styles.FieldHint}`}>{hint}</span>}
  </div>);
};

const ChargeCard = ({ service, control, errors }) => {
  const charge = useWatch({ control, name: service.key });
  const [miles, setMiles] = useState("10");
  const { total, breakdown } = calculateCharge(charge, miles);
  const name = (field) => `${service.key}.${field}`;

  return (<section className={`${styles.Card}`}>
    <div className={`${styles.CardHead}`}>
      <div>
        <h2 className={`${styles.CardTitle}`}>{service.icon}{service.title}</h2>
        <p className={`${styles.CardSubtitle}`}>{service.subtitle}</p>
      </div>
      <div className={`${styles.ChargeHeadRight}`}>
        <span className={`${styles.StatusPill} ${charge?.enabled ? styles.StatusOn : styles.StatusOff}`}>{charge?.enabled ? "On" : "Off"}</span>
        <Controller
          name={name("enabled")}
          control={control}
          render={({ field }) => (
            <Tooltip title={field.value ? "Turn off" : "Turn on"}>
              <Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} color="error" inputProps={{ "aria-label": `Enable ${service.title.toLowerCase()}` }} />
            </Tooltip>
          )}
        />
      </div>
    </div>

    <div className={`${styles.CardBody} ${charge?.enabled ? "" : styles.Disabled}`}>
      <Controller
        name={name("type")}
        control={control}
        render={({ field }) => (
          <ToggleButtonGroup exclusive value={field.value} onChange={(e, v) => v && field.onChange(v)} className={`${styles.TypeToggle}`} aria-label="Charge type">
            <ToggleButton value="fixed"><Tag /> Fixed</ToggleButton>
            <ToggleButton value="mile"><Navigation /> Per mile</ToggleButton>
          </ToggleButtonGroup>
        )}
      />

      {charge?.type === "fixed" ? (
        <div className={`${styles.FormGrid}`}>
          <div className={`${styles.FormFull}`}>
            <MoneyField control={control} errors={errors} name={name("fixed_amount")} label="Flat rate*" requiredType="fixed" hint="Charged once per booking." />
          </div>
        </div>
      ) : (
        <div className={`${styles.FormGrid}`}>
          <MoneyField control={control} errors={errors} name={name("rate_per_mile")} label="Rate per mile*" suffix="/ mi" requiredType="mile" />
          <MoneyField control={control} errors={errors} name={name("minimum_charge")} label="Minimum charge" hint="Optional. Lowest amount ever charged." />
          <MoneyField control={control} errors={errors} name={name("base_charge")} label="Base charge" hint="Optional. Charged on every booking." />
          <MoneyField control={control} errors={errors} name={name("base_miles")} label="Miles included in base" suffix="mi" hint="Optional. Per-mile rate starts after these." />
        </div>
      )}

      <div className={`${styles.Estimator}`}>
        <div className={`${styles.EstimatorHead}`}>
          <span>Example:</span>
          {charge?.type === "mile" && (<>
            <TextField size="small" className={`${styles.EstimatorInput}`} value={miles} onChange={(e) => setMiles(e.target.value.replace(/[^\d.]/g, ""))} inputProps={{ inputMode: "decimal", "aria-label": "Example distance in miles" }} />
            <span>miles</span>
          </>)}
          <span className={`${styles.EstimatorTotal}`}>{gbp(total)}</span>
        </div>
        <p className={`${styles.EstimatorBreakdown}`}>{breakdown}</p>
      </div>
    </div>
  </section>);
};

const ChargeSettings = ({ charges, onSaved, showMessage }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [saving, setSaving] = useState(false);

  const { handleSubmit, control, reset, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: { collection: toFormCharge(null), delivery: toFormCharge(null) },
  });

  useEffect(() => {
    reset({ collection: toFormCharge(charges?.collection), delivery: toFormCharge(charges?.delivery) });
  }, [charges, reset]);

  const onSubmit = (data) => {
    setSaving(true);
    axios.post(process.env.REACT_APP_API_URL + "v1/company-settings/charges", data, {
      headers: { Authorization: `Bearer ${API_TOKEN}` }
    }).then((response) => {
      setSaving(false);
      if (response.data.status === "SUCCESS") {
        showMessage(response.data.message, "success");
        onSaved(response.data.data);
      } else {
        showMessage(response.data.message, "error");
      }
    }).catch(() => {
      setSaving(false);
      showMessage("Could not save charges. Please try again.", "error");
    });
  };

  return (<form onSubmit={handleSubmit(onSubmit)} noValidate>
    <div className={`${styles.Grid}`}>
      {SERVICES.map((service) => (<ChargeCard key={service.key} service={service} control={control} errors={errors} />))}
    </div>
    <div className={`${styles.CardFooter}`} style={{ borderTop: "none", padding: "18px 0 0" }}>
      <button type="submit" className={`${styles.ActionBU} ${styles.ActionPrimary}`} disabled={saving}><Save /> {saving ? "Saving..." : "Save Charges"}</button>
    </div>
  </form>);
};

export default ChargeSettings;
